/**
 * useAnnounce - Composable for screen reader announcements
 * 
 * WCAG 4.1.3: Status Messages
 * Provides a way to programmatically announce content changes
 * to assistive technologies without requiring focus.
 *
 * De teksten lopen via i18nGlobal en niet via useI18n(): deze composable
 * wordt ook buiten een setup-context aangeroepen.
 */

import { i18nGlobal } from '../i18n'

const t = (key: string, params?: Record<string, unknown>) =>
  params ? i18nGlobal.t(key, params) : i18nGlobal.t(key)

export function useAnnounce() {
  /**
   * Announce a polite message (doesn't interrupt current speech)
   * Use for: success messages, status updates, non-critical info
   */
  function announcePolite(message: string) {
    if (typeof window !== 'undefined' && (window as any).__announcePolite) {
      (window as any).__announcePolite(message)
    }
  }

  /**
   * Announce an assertive message (interrupts current speech)
   * Use for: errors, urgent updates, time-sensitive info
   */
  function announceAssertive(message: string) {
    if (typeof window !== 'undefined' && (window as any).__announceAssertive) {
      (window as any).__announceAssertive(message)
    }
  }

  /**
   * Announce a loading state
   */
  function announceLoading(itemName?: string) {
    announcePolite(t('a11y.announce.loading', { item: itemName ?? t('a11y.announce.defaultItem') }))
  }

  /**
   * Announce a loading complete state
   */
  function announceLoaded(itemName?: string) {
    announcePolite(t('a11y.announce.loaded', { item: itemName ?? t('a11y.announce.defaultItem') }))
  }

  /**
   * Announce a success action
   */
  function announceSuccess(action: string) {
    announcePolite(t('a11y.announce.success', { action }))
  }

  /**
   * Announce an error
   */
  function announceError(error: string) {
    announceAssertive(t('a11y.announce.error', { error }))
  }

  /**
   * Announce list item count
   */
  function announceListCount(count: number, itemType: string = 'items') {
    announcePolite(
      count === 0
        ? t('a11y.announce.listNone', { items: itemType })
        : t('a11y.announce.listCount', { count, items: itemType })
    )
  }

  /**
   * Announce navigation
   */
  function announceNavigation(destination: string) {
    announcePolite(t('a11y.announce.navigation', { destination }))
  }

  /**
   * Announce dialog open
   */
  function announceDialogOpen(dialogTitle: string) {
    announcePolite(t('a11y.announce.dialogOpen', { title: dialogTitle }))
  }

  /**
   * Announce dialog close
   */
  function announceDialogClose() {
    announcePolite(t('a11y.announce.dialogClose'))
  }

  /**
   * Announce form submission
   */
  function announceFormSubmitting() {
    announcePolite(t('a11y.announce.formSubmitting'))
  }

  /**
   * Announce form submitted successfully
   */
  function announceFormSubmitted() {
    announcePolite(t('a11y.announce.formSubmitted'))
  }

  /**
   * Announce clipboard copy
   */
  function announceCopied(itemName?: string) {
    announcePolite(t('a11y.announce.copied', { item: itemName ?? t('a11y.announce.defaultItem') }))
  }

  return {
    // Core methods
    announcePolite,
    announceAssertive,
    
    // Convenience methods
    announceLoading,
    announceLoaded,
    announceSuccess,
    announceError,
    announceListCount,
    announceNavigation,
    announceDialogOpen,
    announceDialogClose,
    announceFormSubmitting,
    announceFormSubmitted,
    announceCopied,
  }
}
